import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Card,
  Textarea,
} from '@wove/web-ui'
import { Play, Check, X } from 'lucide-react'
import { testValidationRule } from '@wove/api'
import { toast } from 'sonner'

interface RuleTesterProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  rule: {
    conditions: any
    requirements: any
  }
}

const SAMPLE_DATA_TEMPLATES = {
  air: {
    shipmentType: 'Air',
    origin: { country: 'US', type: 'Airport' },
    destination: { country: 'DE', type: 'Airport' },
    commodity: 'Electronics',
    weight: 100,
    volume: 2.5,
    hazardousMaterials: false,
    importExport: 'Export',
    // Example custom fields
    customFields: {
      po_number: 'PO-2024-001',
      special_handling: 'Fragile',
      customer_reference: 'REF-12345',
    },
  },
  fcl: {
    shipmentType: 'FCL',
    origin: { country: 'CN', type: 'Port' },
    destination: { country: 'US', type: 'Port' },
    loadingPort: 'Shanghai',
    unloadingPort: 'Los Angeles',
    equipmentType: '40HC',
    commodity: 'General Cargo',
    weight: 20000,
    quantity: 1,
    readyDate: new Date().toISOString().split('T')[0],
    // Example custom fields
    customFields: {
      booking_number: 'BK-2024-FCL-001',
      seal_number: 'SEAL123456',
      temperature_control: false,
    },
  },
  lcl: {
    shipmentType: 'LCL',
    origin: { country: 'GB', type: 'Warehouse' },
    destination: { country: 'AU', type: 'Warehouse' },
    loadingPort: 'London',
    unloadingPort: 'Sydney',
    packageLines: 10,
    totalWeight: 500,
    totalVolume: 5,
    commodity: 'Mixed Goods',
    hazardousMaterials: false,
    sailingDate: new Date().toISOString().split('T')[0],
    // Example custom fields
    customFields: {
      consolidation_ref: 'CONSOL-2024-001',
      warehouse_receipt: 'WR-12345',
      stackable: true,
    },
  },
}

export function RuleTester({ open, onOpenChange, rule }: RuleTesterProps) {
  const [testData, setTestData] = useState<string>(
    JSON.stringify(SAMPLE_DATA_TEMPLATES.air, null, 2),
  )
  const [testResults, setTestResults] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<string>('air')

  const handleTemplateChange = (template: string) => {
    setSelectedTemplate(template)
    setTestData(
      JSON.stringify(
        SAMPLE_DATA_TEMPLATES[template as keyof typeof SAMPLE_DATA_TEMPLATES],
        null,
        2,
      ),
    )
    setTestResults(null)
  }

  const handleTest = async () => {
    try {
      setIsLoading(true)

      // Parse the test data
      let parsedData
      try {
        parsedData = JSON.parse(testData)
      } catch (e) {
        toast.error('Invalid JSON in test data')
        return
      }

      const response = await testValidationRule({
        conditions: rule.conditions,
        requirements: rule.requirements,
        sampleData: parsedData,
      })

      setTestResults(response)
    } catch (error) {
      toast.error('Failed to test rule')
    } finally {
      setIsLoading(false)
    }
  }

  const renderFieldState = (fieldPath: string, state: string) => {
    const variant =
      state === 'required' ? 'destructive' : state === 'optional' ? 'secondary' : 'outline'
    return (
      <div
        key={fieldPath}
        className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded"
      >
        <span className="font-medium text-sm">{fieldPath}</span>
        <Badge variant={variant} className="text-xs">
          {state}
        </Badge>
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle>Test Validation Rule</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-2 gap-6">
            {/* Left side - Test Data Input */}
            <div className="space-y-4">
              <div>
                <Label>Test Data Template</Label>
                <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="air">Air Freight</SelectItem>
                    <SelectItem value="fcl">FCL (Full Container)</SelectItem>
                    <SelectItem value="lcl">LCL (Less than Container)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Test Data (JSON)</Label>
                <Textarea
                  value={testData}
                  onChange={(e) => setTestData(e.target.value)}
                  className="mt-2 font-mono text-sm"
                  rows={20}
                  placeholder="Enter test data in JSON format"
                />
              </div>

              <Button onClick={handleTest} disabled={isLoading} className="w-full">
                <Play className="h-4 w-4 mr-2" />
                {isLoading ? 'Testing...' : 'Run Test'}
              </Button>
            </div>

            {/* Right side - Test Results */}
            <div className="space-y-4">
              <h3 className="font-medium">Test Results</h3>

              {!testResults ? (
                <Card className="p-6 text-center text-gray-500">
                  Run a test to see results here
                </Card>
              ) : (
                <div className="space-y-4">
                  {/* Condition Match Result */}
                  <Card className="p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Conditions Match</span>
                      {testResults.conditionsMatch ? (
                        <Badge variant="success" className="flex items-center gap-1">
                          <Check className="h-3 w-3" />
                          Yes
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="flex items-center gap-1">
                          <X className="h-3 w-3" />
                          No
                        </Badge>
                      )}
                    </div>
                  </Card>

                  {/* Applied Requirements */}
                  {testResults.conditionsMatch && (
                    <>
                      <Card className="p-4">
                        <h4 className="font-medium mb-3">Applied Requirements</h4>
                        <div className="space-y-2">
                          {testResults.appliedRequirements.length === 0 ? (
                            <p className="text-sm text-gray-500">No requirements applied</p>
                          ) : (
                            testResults.appliedRequirements.map((req: any, index: number) => (
                              <div
                                key={index}
                                className="text-sm border-l-2 border-blue-500 pl-3 py-1"
                              >
                                <div className="font-medium">{req.fieldPath}</div>
                                <div className="text-gray-600">
                                  Type:{' '}
                                  <Badge variant="outline" className="text-xs ml-1">
                                    {req.requirementType}
                                  </Badge>
                                  Context:{' '}
                                  <Badge variant="outline" className="text-xs ml-1">
                                    {req.context}
                                  </Badge>
                                </div>
                                {req.errorMessage && (
                                  <div className="text-gray-500 mt-1">
                                    Error: {req.errorMessage}
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </Card>

                      <Card className="p-4">
                        <h4 className="font-medium mb-3">Field States After Rule Application</h4>
                        <div className="space-y-2">
                          {Object.entries(testResults.fieldStates).map(([field, state]) =>
                            renderFieldState(field, state as string),
                          )}
                        </div>
                      </Card>
                    </>
                  )}

                  {/* Raw Test Data for verification */}
                  <details className="mt-4">
                    <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                      View raw test response
                    </summary>
                    <pre className="mt-2 text-xs bg-gray-100 p-3 rounded overflow-auto">
                      {JSON.stringify(testResults, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
